import { useState, useMemo } from "react";
import { Select, SelectItem } from "@heroui/react";
import { K1FundsButton } from "@/components/ui/k1-button";
import { K1SectionLabel } from "@/components/ui/k1-typography";
import { useK1DashboardContext } from "../context/K1DashboardProvider";
import { K1FundsModal } from "./K1FundsModal";

interface K1FilterBarProps {
  modalOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
  singleSelect?: boolean;
  hideAll?: boolean;
  hidePeriod?: boolean;
  showPeriod?: boolean;
  hideFundsButton?: boolean;
}

export default function K1FilterBar({
  modalOpen,
  onModalOpenChange,
  singleSelect = false,
  hideAll = false,
  hidePeriod = false,
  showPeriod = true,
  hideFundsButton = false,
}: K1FilterBarProps) {
  const {
    funds,
    setFundsModalOpen,
    selectedFundIds,
    setSelectedFundIds,
    fundSelection,
    setFundSelection,
    selectedPeriod,
    setSelectedPeriod,
    availablePeriods,
    periodsLoading,
  } = useK1DashboardContext();

  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = modalOpen ?? internalOpen;
  const setOpen = onModalOpenChange ?? setInternalOpen;
  const shouldShowPeriod = !hidePeriod && showPeriod;
  const shouldHideFundsButton = hideAll || hideFundsButton;

  const selectedFunds = useMemo(
    () =>
      // Preserve selection order
      selectedFundIds
        .map((id) => funds.find((f) => (f.id ?? f.fund_id) === id))
        .filter(Boolean) as typeof funds,
    [funds, selectedFundIds],
  );

  // In multi-select mode: summary label
  const summaryName = useMemo(() => {
    if (selectedFunds.length === 0) return null;
    if (selectedFunds.length <= 2)
      return selectedFunds.map((f) => f.fund_name).join(", ");
    return `${selectedFunds[0].fund_name} +${selectedFunds.length - 1}`;
  }, [selectedFunds]);

  // Reorder: make the clicked fund the active one (position 0)
  const handleChipClick = (id: number) => {
    if (selectedFundIds[0] === id) return;
    setSelectedFundIds([id, ...selectedFundIds.filter((x) => x !== id)]);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-none">
        <div className="flex min-w-0 items-center gap-3">
          {!shouldHideFundsButton && (
            <>
              <K1FundsButton
                onPress={() => { setOpen(true); setFundsModalOpen(true); }}
                isActive={selectedFundIds.length > 0}
              />
              <div className="h-7 w-px shrink-0 bg-slate-200" />
            </>
          )}

          {selectedFundIds.length === 0 ? (
            <div>
              <span className="text-sm text-slate-400">No fund selected</span>
              {singleSelect && (
                <button
                  onClick={() => { setOpen(true); setFundsModalOpen(true); }}
                  className="ml-2 text-xs font-medium text-blue-500 hover:text-blue-700 underline"
                >
                  Select a fund
                </button>
              )}
            </div>
          ) : singleSelect ? (
            // ── Single-select mode: chips row ──────────────────────────────
            <div className="flex min-w-0 flex-col gap-1">
              <div
                className="flex items-center gap-1.5 overflow-x-auto pb-0.5"
                style={{ scrollbarWidth: "none" }}
              >
                {selectedFunds.map((fund, idx) => {
                  const id = fund.id ?? fund.fund_id;
                  const isActive = idx === 0;
                  return (
                    <button
                      key={id}
                      onClick={() => handleChipClick(id)}
                      title={
                        isActive
                          ? "Active fund"
                          : "Click to switch to this fund"
                      }
                      className={[
                        "flex shrink-0 items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition-all",
                        isActive
                          ? "border-blue-200 bg-blue-50 text-blue-700 cursor-default shadow-sm"
                          : "border-slate-200 bg-slate-50 text-slate-400 hover:border-slate-300 hover:text-slate-600 cursor-pointer",
                      ].join(" ")}
                    >
                      {isActive && (
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                      )}
                      <span className="max-w-[160px] truncate">
                        {fund.fund_name}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 leading-none">
                {selectedFunds.length > 1
                  ? `Single-fund view · click another fund to switch`
                  : "Single-fund view"}
              </p>
            </div>
          ) : (
            // ── Multi-select mode: summary ─────────────────────────────────
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-slate-800">
                {summaryName}
              </div>
              <div className="text-xs text-slate-500">
                {selectedFundIds.length === 1
                  ? "1 fund selected"
                  : `${selectedFundIds.length} funds selected`}
              </div>
            </div>
          )}
        </div>

        {shouldShowPeriod && selectedFundIds.length > 0 && (
          <div className="flex shrink-0 items-center gap-2">
            <K1SectionLabel className="text-slate-400">Period</K1SectionLabel>
            {periodsLoading ? (
              <span className="text-xs text-slate-400">Loading…</span>
            ) : availablePeriods.length > 0 ? (
              <Select
                size="sm"
                selectedKeys={
                  selectedPeriod ? new Set([selectedPeriod]) : new Set()
                }
                onSelectionChange={(keys) =>
                  setSelectedPeriod((Array.from(keys)[0] as string) ?? null)
                }
                aria-label="Reporting period"
                classNames={{
                  trigger:
                    "h-8 min-h-8 w-28 shadow-none !rounded-md border-slate-200 text-[12px]",
                  value: "text-[12px]",
                }}
              >
                {availablePeriods.map((p) => (
                  <SelectItem key={p}>{p}</SelectItem>
                ))}
              </Select>
            ) : null}
          </div>
        )}
      </div>

      <K1FundsModal
        isOpen={isOpen}
        onOpenChange={setOpen}
        funds={funds}
        selectedIds={selectedFundIds}
        selection={fundSelection}
        onApply={setFundSelection}
        singleSelect={singleSelect}
        hideAll={hideAll}
      />
    </>
  );
}
